import { Dayjs } from 'dayjs';

import { useAuthContext, useUpdateMealDetails, useUpdateMealPrediction } from '../data-access';
import {
  MealType,
  type MealFoodAmount,
  type MetricMealDetailMacros,
  type MetricMealDetailMicros,
} from '@demind-inc/core';
import dayjs from 'dayjs';
import { trackEventMixpanel } from '../utils';

interface UseEditMealParams {
  date: Dayjs;
}

export interface EditMealParam {
  mealType: MealType;
  startTime: string;
  endTime: string;
  mealContent?: string;
  title?: string;
  foodItems?: MealFoodAmount[];
  macros?: MetricMealDetailMacros;
  micros?: MetricMealDetailMicros;
}

export interface EditMealPredictionParam {
  newData: EditMealParam;
  shouldUpdateEvent?: boolean;
  description?: string;
  baseCalendarId?: string;
}

export const useEditMeal = ({ date }: UseEditMealParams) => {
  const { updateMealDetail, isPending: isEditingData } = useUpdateMealDetails();
  const { updateMealPrediction, isPending: isUpdatingMealPrediction } = useUpdateMealPrediction();
  const { user } = useAuthContext();

  const handleEditMealData = async (newData: EditMealParam) => {
    const targetDate = dayjs(newData.startTime).format('YYYY-MM-DD');
    const mealContent = newData.mealContent?.trim();
    const hasFoodItems = Array.isArray(newData.foodItems) && newData.foodItems.length > 0;
    const foodItems = hasFoodItems
      ? newData.foodItems
      : mealContent
        ? [{ name: mealContent, amount: 1 }]
        : undefined;
    const fallbackFood = mealContent
      ? { name: mealContent, amount: 1 }
      : foodItems?.[0]
        ? { name: foodItems[0].name, amount: foodItems[0].amount }
        : undefined;
    const hasMacros = !!newData.macros && Object.keys(newData.macros).length > 0;
    const hasMicros = !!newData.micros && Object.keys(newData.micros).length > 0;

    await updateMealDetail({
      userId: user.userId,
      metricId: user.metricId!,
      detailsByDate: [
        {
          date: targetDate,
          details: [
            {
              type: newData.mealType,
              startTime: dayjs(newData.startTime).format('YYYY-MM-DDTHH:mm:ss'),
              endTime: dayjs(newData.endTime).format('YYYY-MM-DDTHH:mm:ss'),
              ...(mealContent ? { title: mealContent } : {}),
              ...(newData.title?.trim() ? { title: newData.title.trim() } : {}),
              ...(foodItems ? { foodItems } : {}),
              ...(fallbackFood ? { food: fallbackFood } : {}),
              ...(hasMacros ? { macros: newData.macros } : {}),
              ...(hasMicros ? { micros: newData.micros } : {}),
            },
          ],
        },
      ],
    });
    trackEventMixpanel('edit_meal_data', { ...newData });
  };

  const handleEditMealPrediction = async ({
    newData,
    shouldUpdateEvent = true,
    description = '',
    baseCalendarId = '',
  }: EditMealPredictionParam) => {
    const targetDate = dayjs(newData.startTime).format('YYYY-MM-DD');
    const mealContent = newData.mealContent?.trim();
    const hasFoodItems = Array.isArray(newData.foodItems) && newData.foodItems.length > 0;
    const foodItems = hasFoodItems
      ? newData.foodItems
      : mealContent
        ? [{ name: mealContent, amount: 1 }]
        : undefined;
    const hasMacros = !!newData.macros && Object.keys(newData.macros).length > 0;
    const hasMicros = !!newData.micros && Object.keys(newData.micros).length > 0;

    await updateMealPrediction({
      metricId: user.metricId!,
      date: targetDate,
      prediction: {
        [newData.mealType]: {
          startTime: dayjs(newData.startTime).format('HH:mm'),
          endTime: dayjs(newData.endTime).format('HH:mm'),
          description,
          ...(mealContent ? { title: mealContent } : {}),
          ...(newData.title?.trim() ? { title: newData.title.trim() } : {}),
          ...(foodItems ? { foodItems } : {}),
          ...(hasMacros ? { macros: newData.macros } : {}),
          ...(hasMicros ? { micros: newData.micros } : {}),
        },
      },
      shouldUpdateEvent,
      baseCalendarId,
      editedManually: true,
    });
    trackEventMixpanel('edit_meal_prediction', { ...newData });
  };

  return { handleEditMealData, handleEditMealPrediction, isEditingData, isUpdatingMealPrediction };
};
